/**
 * Invoice-specific utility functions for variable replacement
 */

import {
    replaceVariables,
    formatCurrency,
    formatDate,
    formatDateLong,
    getCurrencySymbol,
    getPhoneNumbers,
    calculateDaysDiff,
} from "./variable-replacer";
import Vue from "vue";

export function prepareInvoiceData(invoice, billingAccount, translate) {
    if (!invoice) {
        return {};
    }

    // Use provided items or fallback to invoice.items_count for count
    const itemsCount = invoice.items_count || 0;
    const data = {
        // Invoice information
        invoice_id: invoice.invoice_id,
        invoice_date: formatDate(invoice.invoice_date),
        invoice_date_long: formatDateLong(invoice.invoice_date),
        due_date: formatDate(invoice.due_date),
        due_date_long: formatDateLong(invoice.due_date),
        days_overdue: calculateDaysDiff(invoice.due_date),

        // Amounts
        total_amount_excl_vat: formatCurrency(
            invoice.total_amount_excl_vat || invoice.total_excl_vat,
            invoice.currency
        ),
        total_amount_incl_vat: formatCurrency(
            invoice.total_amount_incl_vat || invoice.total_incl_vat,
            invoice.currency
        ),
        total_vat_amount: formatCurrency(
            invoice.total_vat_amount || invoice.vat_amount,
            invoice.currency
        ),
        currency: invoice.currency || "EUR",
        currency_symbol: getCurrencySymbol(invoice.currency || "EUR"),

        // Parties (Customer information)
        billed_name: invoice.billed_data?.name || invoice.billed?.name || "",
        billing_address:
            invoice.billed_data?.billing_address?.full ||
            invoice.billed?.billing_address?.full ||
            "",
        contact_email:
            invoice.billed_data?.billing_address?.emails?.[0] ||
            invoice.billed?.billing_address?.emails?.[0] ||
            "",
        contact_phone_numbers: getPhoneNumbers(
            invoice.billed_data?.billing_address
        ),

        // Issuer information
        issuer_name: billingAccount?.name || "",
        issuer_contact_email: billingAccount?.address?.emails?.[0] || "",
        issuer_contact_phone_numbers: getPhoneNumbers(billingAccount?.address),
        issuer_address: billingAccount?.address?.full || "",

        // Items
        items_count: itemsCount,
    };

    return data;
}

/**
 * Replace variables in invoice text template
 * @param {string} template - The text template containing variables like {{variable_name}}
 * @param {Object} invoice - The invoice object
 * @param {Object} billingAccount - The billing account object
 * @param {Function} translate - Translation function (optional)
 * @returns {string} - The text with variables replaced
 */
export function replaceInvoiceVariables(
    template,
    invoice,
    billingAccount,
    translate = null
) {
    const data = prepareInvoiceData(invoice, billingAccount, translate);
    return replaceVariables(template, data);
}

export function getInvoiceVariables(invoice, billingAccount) {
    // Get variable categories from billing account
    if (!billingAccount || !invoice) return [];

    // Calculate values once to avoid recalculation on every render
    const daysOverdue = calculateDaysDiff(invoice.due_date);
    const currencySymbol = getCurrencySymbol(invoice.currency || "EUR");

    return [
        {
            name: "invoice_info",
            label: Vue.prototype.$translate("variables.invoiceInformation"),
            variables: [
                {
                    value: invoice.invoice_id || invoice.id,
                    label: Vue.prototype.$translate("variables.invoiceId"),
                },
                {
                    value: invoice.invoice_date,
                    label: Vue.prototype.$translate("variables.invoiceDate"),
                },
                {
                    value: formatDateLong(invoice.invoice_date),
                    label: Vue.prototype.$translate("variables.invoiceDateLong"),
                },
                {
                    value: invoice.due_date,
                    label: Vue.prototype.$translate("variables.dueDate"),
                },
                {
                    value: formatDateLong(invoice.due_date),
                    label: Vue.prototype.$translate("variables.dueDateLong"),
                },
                {
                    value: daysOverdue,
                    label: Vue.prototype.$translate("variables.daysOverdue"),
                },
            ],
        },
        {
            name: "amounts",
            label: Vue.prototype.$translate("variables.amounts"),
            variables: [
                {
                    value:
                        invoice.total_amount_excl_vat || invoice.total_excl_vat,
                    label: Vue.prototype.$translate("variables.totalExclVat"),
                },
                {
                    value:
                        invoice.total_amount_incl_vat || invoice.total_incl_vat,
                    label: Vue.prototype.$translate("variables.totalInclVat"),
                },
                {
                    value: invoice.total_vat_amount || invoice.vat_amount,
                    label: Vue.prototype.$translate("variables.totalVatAmount"),
                },
                {
                    value: invoice.currency || "EUR",
                    label: Vue.prototype.$translate("variables.currency"),
                },
                {
                    value: currencySymbol,
                    label: Vue.prototype.$translate("variables.currencySymbol"),
                },
            ],
        },
        {
            name: "issuer",
            label: Vue.prototype.$translate("variables.issuer"),
            variables: [
                {
                    value: billingAccount?.name || "",
                    label: Vue.prototype.$translate("variables.issuerName"),
                },
                {
                    value: billingAccount?.address?.emails?.[0] || "",
                    label: Vue.prototype.$translate("variables.issuerContactEmail"),
                },
                {
                    value: billingAccount?.address?.phone_number || "",
                    label: Vue.prototype.$translate("variables.issuerContactPhoneNumbers"),
                },
                {
                    value: billingAccount?.address?.full || "",
                    label: Vue.prototype.$translate("variables.issuerAddress"),
                },
            ],
        },
        {
            name: "billed",
            label: Vue.prototype.$translate("variables.billed"),
            variables: [
                {
                    value:
                        invoice.billed_data?.name || invoice.billed?.name || "",
                    label: Vue.prototype.$translate("variables.billedName"),
                },
                {
                    value:
                        invoice.billed_data?.billing_address?.full ||
                        invoice.billed?.billing_address?.full ||
                        "",
                    label: Vue.prototype.$translate("variables.billingAddress"),
                },
                {
                    value:
                        invoice.billed_data?.billing_address?.emails?.[0] ||
                        invoice.billed?.billing_address?.emails?.[0] ||
                        "",
                    label: Vue.prototype.$translate("variables.contactEmail"),
                },
                {
                    value: getPhoneNumbers(invoice.billed_data?.billing_address),
                    label: Vue.prototype.$translate("variables.contactPhoneNumbers"),
                },
            ],
        },
        {
            name: "items",
            label: Vue.prototype.$translate("variables.items"),
            variables: [
                {
                    value: invoice.items_count || 0,
                    label: Vue.prototype.$translate("variables.itemsCount"),
                },
            ],
        },
    ];
}
